import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { SystemPromptSummary } from '../types/systemPrompt';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface SystemPromptTableDropdownProps {
  prompts: SystemPromptSummary[];
  selectedHash: string;
  onSelect: (hash: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

type SortColumn = 'date' | 'description';
type SortDirection = 'asc' | 'desc';

export const SystemPromptTableDropdown: React.FC<SystemPromptTableDropdownProps> = ({
  prompts,
  selectedHash,
  onSelect,
  disabled = false,
  placeholder = 'Default (always latest)'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const selectedPrompt = prompts.find(p => p.hash === selectedHash);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [isOpen]);

  const handleHeaderClick = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      const defaultDir: SortDirection = column === 'date' ? 'desc' : 'asc';
      setSortDirection(defaultDir);
    }
  }, [sortColumn]);

  const sortedPrompts = useMemo(() => {
    const sorted = [...prompts];
    sorted.sort((a, b) => {
      switch (sortColumn) {
        case 'date': {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        }
        case 'description':
          return sortDirection === 'asc'
            ? a.description.localeCompare(b.description)
            : b.description.localeCompare(a.description);
        default:
          return 0;
      }
    });
    return sorted;
  }, [prompts, sortColumn, sortDirection]);

  const SortIndicator: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div data-component="SystemPromptTableDropdown" ref={containerRef} className="relative flex-1">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <span className={selectedPrompt ? 'text-gray-900 truncate' : 'text-gray-500'}>
          {selectedPrompt
            ? `[${new Date(selectedPrompt.createdAt).toLocaleDateString()}] ${selectedPrompt.description}`
            : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {/* Dropdown table */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-max min-w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
          {/* Default option row */}
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th
                  className="px-3 py-2 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleHeaderClick('date')}
                >
                  <div className="flex items-center gap-1">
                    <span>Date</span>
                    <SortIndicator column="date" />
                  </div>
                </th>
                <th
                  className="px-3 py-2 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleHeaderClick('description')}
                >
                  <div className="flex items-center gap-1">
                    <span>Description</span>
                    <SortIndicator column="description" />
                  </div>
                </th>

              </tr>
            </thead>
            <tbody>
              {/* Default / no-selection row */}
              <tr
                onClick={() => { onSelect(''); setIsOpen(false); }}
                className={`cursor-pointer hover:bg-blue-50 border-t border-gray-100 ${
                  !selectedHash ? 'bg-blue-100' : ''
                }`}
              >
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">—</td>
                <td className="px-3 py-2 text-gray-500 italic whitespace-nowrap">Default (always latest)</td>
              </tr>
              {sortedPrompts.map(prompt => {
                const isSelected = prompt.hash === selectedHash;
                return (
                  <tr
                    key={prompt.hash}
                    ref={isSelected ? selectedRowRef : undefined}
                    onClick={() => { onSelect(prompt.hash); setIsOpen(false); }}
                    className={`cursor-pointer hover:bg-blue-50 border-t border-gray-100 ${
                      isSelected ? 'bg-blue-100' : ''
                    }`}
                  >
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(prompt.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap" title={prompt.purpose}>
                      {prompt.description}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
